import { Camera, MapPin } from 'lucide-react';
import { useState } from 'react';

import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { PhotoLightbox, type LightboxPhoto } from '../components/ui/PhotoLightbox';
import { usePageMeta } from '../lib/usePageMeta';

interface GalleryItem extends LightboxPhoto {
  title: string;
  caption: string;
  hero?: boolean;
}

interface GallerySectionData {
  eyebrow: string;
  title: string;
  lead: string;
  items: GalleryItem[];
}

const sections: GallerySectionData[] = [
  {
    eyebrow: 'I. From Above',
    title: 'The property in context',
    lead: 'Aerial passes of the building, the grounds, and the Banana River channel.',
    items: [
      {
        src1600: '/photos/image4-1600.webp',
        src800: '/photos/image4-800.webp',
        alt: 'Drone view of Sunset Harbor from above the Banana River, the building set against palm-lined grounds with the Atlantic mainland reaching to the horizon.',
        title: 'East-side approach',
        caption: 'Drone over the Banana River, looking back at the property.',
        hero: true,
      },
      {
        src1600: '/photos/image3-1600.webp',
        src800: '/photos/image3-800.webp',
        alt: 'Aerial east-side view of Sunset Harbor showing the full façade, address 3873, the rooftop wordmark, and the marina and causeway across the Banana River channel.',
        title: 'East façade',
        caption: 'Address 3873. Rooftop wordmark. Marina across the channel.',
      },
      {
        src1600: '/photos/image5-1600.webp',
        src800: '/photos/image5-800.webp',
        alt: 'High aerial of the Sunset Harbor property in its Cocoa Beach setting, showing the building, carports, neighboring condominiums to the west, and the marina to the east on the Banana River.',
        title: 'In context',
        caption: 'Cocoa Beach setting: building, carports, marina, river.',
      },
      {
        src1600: '/photos/image10-1600.webp',
        src800: '/photos/image10-800.webp',
        alt: "Aerial of Sunset Harbor's east elevation with the Banana River filling the background, the mainland visible on the horizon, and palm-lined grounds in the foreground.",
        title: 'East elevation',
        caption: 'The river fills the frame; mainland on the horizon.',
      },
      {
        src1600: '/photos/image0-1600.webp',
        src800: '/photos/image0-800.webp',
        alt: "Aerial of Sunset Harbor's west side over the seawall lagoon, with palms along the grass, the building's west elevation, and the Cocoa Beach mainland reaching to the Atlantic in the distance.",
        title: 'West side',
        caption: 'Seawall lagoon, palms, the building’s west elevation.',
      },
    ],
  },
  {
    eyebrow: 'II. Grounds & Water',
    title: 'On the property',
    lead: 'Palm-lined grounds, the pool deck, the dock, the carports.',
    items: [
      {
        src1600: '/photos/image7-1600.webp',
        src800: '/photos/image7-800.webp',
        alt: "Aerial of Sunset Harbor's east elevation showing palm-lined grass, the pool deck in the lower right, and the wide Banana River with mainland mangroves beyond.",
        title: 'Pool deck & lawn',
        caption: 'Palm-lined grass, the pool deck, the wide river behind.',
        hero: true,
      },
      {
        src1600: '/photos/image6-1600.webp',
        src800: '/photos/image6-800.webp',
        alt: "Aerial from the road side looking west across Sunset Harbor's carports toward the Banana River, with palm-lined grounds along the building.",
        title: 'Road side',
        caption: 'Carports under the palms; building shielding the river.',
      },
      {
        src1600: '/photos/image9-1600.webp',
        src800: '/photos/image9-800.webp',
        alt: "Aerial view of Sunset Harbor's outdoor pool, fenced and lined with chaise lounges, framed by palms on the seawall with the Banana River and neighboring marina beyond.",
        title: 'Pool',
        caption: 'Fenced, palm-shaded, the seawall a few steps away.',
      },
      {
        src1600: '/photos/image2-1600.webp',
        src800: '/photos/image2-800.webp',
        alt: "Ground-level view of Sunset Harbor's wooden dock extending into the Banana River, with palm trees lining the seawall along the building.",
        title: 'The dock',
        caption: 'Rebuilt 2024 by Darrell’s Docks. Coquina rock revetment.',
      },
    ],
  },
  {
    eyebrow: 'III. Golden Hour',
    title: 'Sunsets on the river',
    lead: 'The name is not a coincidence. Looking west across the channel after the day cools.',
    items: [
      {
        src1600: '/photos/image8-1600.webp',
        src800: '/photos/image8-800.webp',
        alt: 'Sunset over the pool at Sunset Harbor: pink and orange clouds behind silhouetted palms, empty pool deck and chaise lounges in the foreground, the river beyond the fence.',
        title: 'Pool at sunset',
        caption: 'Pink sky over the pool, palms in silhouette.',
      },
      {
        src1600: '/photos/image1-1600.webp',
        src800: '/photos/image1-800.webp',
        alt: 'Sunset over the Banana River, viewed from Sunset Harbor: warm orange sky, silhouetted palms in the foreground, and the neighboring condominiums to the right.',
        title: 'Sunset over the river',
        caption: 'Orange sky, silhouetted palms, neighbors to the right.',
      },
    ],
  },
];

const allPhotos: LightboxPhoto[] = sections.flatMap((s) =>
  s.items.map(({ src1600, src800, alt }) => ({ src1600, src800, alt })),
);

export function Gallery(): JSX.Element {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  usePageMeta();

  let runningIndex = 0;

  return (
    <>
      <PageHero
        eyebrow="The Property"
        title="Gallery"
        lead="Sunset Harbor sits on the Banana River in Cocoa Beach, two blocks from the Atlantic. Forty-five owners. Five stories. One waterfront."
        photo={{
          src1600: '/photos/image4-1600.webp',
          src800: '/photos/image4-800.webp',
          alt: 'Drone view of Sunset Harbor from above the Banana River, the building set against palm-lined grounds with the Atlantic mainland reaching to the horizon.',
          objectPosition: 'center 60%',
        }}
      />

      <Container className="py-12 md:py-16">
        <div className="grid gap-6 rounded-2xl border border-brand-surface bg-brand-white p-6 shadow-card sm:grid-cols-3 sm:p-8">
          {[
            { Icon: Camera, label: 'Photos', value: `${allPhotos.length}` },
            { Icon: MapPin, label: 'Address', value: '3873 Banana River Blvd' },
            { Icon: MapPin, label: 'City', value: 'Cocoa Beach, FL' },
          ].map(({ Icon, label, value }) => (
            <div key={label} className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-brand-charcoal">
                <Icon size={18} strokeWidth={2} aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                  {label}
                </p>
                <p className="text-base font-semibold text-brand-charcoal">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>

      {sections.map((section, sectionIdx) => {
        const isLastSection = sectionIdx === sections.length - 1;
        return (
          <section
            key={section.title}
            className={`section ${sectionIdx % 2 === 0 ? 'bg-brand-white' : 'bg-brand-surface'}`}
          >
            <Container>
              <div className="mb-10 max-w-2xl md:mb-12">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
                  {section.eyebrow}
                </p>
                <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
                  {section.title}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-brand-body-dark">
                  {section.lead}
                </p>
              </div>

              <ul
                className={
                  isLastSection
                    ? 'grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-2'
                    : 'grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6 lg:grid-cols-3'
                }
              >
                {section.items.map((photo) => {
                  const photoIndex = runningIndex++;
                  const isHero = photo.hero && !isLastSection;
                  return (
                    <li key={photo.src1600} className={isHero ? 'sm:col-span-2 lg:col-span-2' : ''}>
                      <button
                        type="button"
                        onClick={() => setOpenIndex(photoIndex)}
                        className="group relative block w-full overflow-hidden rounded-lg bg-brand-surface shadow-card transition-shadow hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2"
                        style={{ aspectRatio: isHero ? '21 / 9' : '4 / 3' }}
                        aria-label={`${photo.title}, open full size`}
                      >
                        <picture>
                          <source
                            type="image/webp"
                            srcSet={`${photo.src800} 800w, ${photo.src1600} 1600w`}
                            sizes={
                              isHero
                                ? '(min-width: 1024px) 800px, (min-width: 640px) 100vw, 100vw'
                                : '(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw'
                            }
                          />
                          <img
                            src={photo.src800}
                            alt={photo.alt}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out-expo group-hover:scale-[1.04]"
                          />
                        </picture>

                        <div
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/35 to-transparent"
                        />

                        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-5 text-left md:p-6">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-yellow">
                            {photo.title}
                          </p>
                          <p className="text-sm leading-snug text-brand-white md:text-base">
                            {photo.caption}
                          </p>
                        </div>

                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-yellow text-brand-charcoal opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.25"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="15 3 21 3 21 9" />
                            <polyline points="9 21 3 21 3 15" />
                            <line x1="21" y1="3" x2="14" y2="10" />
                            <line x1="3" y1="21" x2="10" y2="14" />
                          </svg>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Container>
          </section>
        );
      })}

      <PhotoLightbox
        photos={allPhotos}
        openIndex={openIndex}
        onClose={() => setOpenIndex(null)}
        onIndexChange={setOpenIndex}
      />
    </>
  );
}
